import { ReactNode, useEffect, useMemo, useState } from 'react';
import Tabs, { type TabItem } from './Tabs';
import { ActivityIcon, ChevronLeftIcon, ChevronRightIcon, FilterIcon, PlusIcon, RefreshIcon, TrashIcon } from './icons';

export interface ListWorkspaceColumn<T> {
  key: string;
  header: ReactNode;
  width?: string;
  align?: 'left' | 'center' | 'right';
  render: (row: T, index: number) => ReactNode;
}

export interface ListWorkspaceProps<T> {
  tabs?: TabItem[];
  activeTabId?: string;
  onTabChange?: (tabId: string) => void;
  headerStatus?: ReactNode;
  headerLabel?: string;
  collapseToolbarWhenEmpty?: boolean;
  rows: T[];
  rowId: (row: T, index: number) => string | number;
  columns: Array<ListWorkspaceColumn<T>>;
  page?: number;
  pageSize?: number;
  totalCount?: number;
  paginationMeta?: ListWorkspacePaginationMeta;
  hidePagination?: boolean;
  pageSizeOptions?: number[];
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
  emptyState?: ReactNode;
  loading?: boolean;
  highlightRowId?: string | number | null;
  onRowClick?: (row: T) => void;
  selectionDisabled?: boolean;
  onSelectionChange?: (selectedIds: Array<string | number>, rows: T[]) => void;
  onAdd?: () => void;
  onDeleteSelected?: (selectedRows: T[]) => void;
  onRefresh?: () => void;
  filterBar?: ReactNode;
  filterDefaultOpen?: boolean;
  onFilterToggle?: (open: boolean) => void;
  onFilterReset?: () => void;
  filterActive?: boolean;
  auditPanel?: ReactNode;
  onAuditToggle?: (open: boolean) => void;
  auditDefaultOpen?: boolean;
  rowActions?: (row: T, index: number) => ReactNode;
  toolbarLeadingActions?: ReactNode;
  toolbarActions?: ReactNode;
}

const DEFAULT_PAGE_SIZE = 10;

export interface ListWorkspacePaginationMeta {
  totalCount: number;
  pageCount: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
  exactTotal: number | null;
}

const ListWorkspace = <T,>({
  tabs,
  activeTabId,
  onTabChange,
  headerStatus,
  headerLabel,
  collapseToolbarWhenEmpty = false,
  rows,
  rowId,
  columns,
  page = 1,
  pageSize = DEFAULT_PAGE_SIZE,
  totalCount,
  paginationMeta,
  hidePagination = false,
  pageSizeOptions = [10, 25, 50],
  onPageChange,
  onPageSizeChange,
  emptyState,
  loading = false,
  highlightRowId = null,
  onRowClick,
  selectionDisabled = false,
  onSelectionChange,
  onAdd,
  onDeleteSelected,
  onRefresh,
  filterBar,
  filterDefaultOpen = false,
  onFilterToggle,
  onFilterReset,
  filterActive = false,
  auditPanel,
  onAuditToggle,
  auditDefaultOpen = false,
  rowActions,
  toolbarLeadingActions,
  toolbarActions
}: ListWorkspaceProps<T>) => {
  const [filtersOpen, setFiltersOpen] = useState(filterDefaultOpen);
  const [auditOpen, setAuditOpen] = useState(auditDefaultOpen);
  const [selectedIds, setSelectedIds] = useState<Set<string | number>>(new Set());
  const [hoveredRowId, setHoveredRowId] = useState<string | number | null>(null);

  useEffect(() => {
    // Remove selections that are not present in current rows
    const visibleIds = new Set(rows.map((row, index) => rowId(row, index)));
    setSelectedIds((previous) => {
      const next = new Set(previous);
      let dirty = false;
      previous.forEach((value) => {
        if (!visibleIds.has(value)) {
          next.delete(value);
          dirty = true;
        }
      });
      return dirty ? next : previous;
    });
  }, [rows, rowId]);

  useEffect(() => {
    if (!onSelectionChange) {
      return;
    }
    const selected = rows.filter((row, index) => selectedIds.has(rowId(row, index)));
    onSelectionChange(Array.from(selectedIds), selected);
  }, [selectedIds, rows, rowId, onSelectionChange]);

  const selectedRows = useMemo(() => rows.filter((row, index) => selectedIds.has(rowId(row, index))), [rows, rowId, selectedIds]);
  const selectedCount = selectedIds.size;

  const handleToggleFilters = () => {
    const next = !filtersOpen;
    setFiltersOpen(next);
    onFilterToggle?.(next);
  };

  const handleToggleAudit = () => {
    const next = !auditOpen;
    setAuditOpen(next);
    onAuditToggle?.(next);
  };

  const handleSelectAll = (checked: boolean) => {
    if (selectionDisabled) {
      return;
    }
    if (!checked) {
      setSelectedIds(new Set());
      return;
    }
    const next = new Set<string | number>();
    rows.forEach((row, index) => {
      next.add(rowId(row, index));
    });
    setSelectedIds(next);
  };

  const handleToggleRow = (id: string | number) => {
    if (selectionDisabled) {
      return;
    }
    setSelectedIds((previous) => {
      const next = new Set(previous);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const allSelected = !selectionDisabled && rows.length > 0 && selectedIds.size === rows.length;
  const displayTotal = paginationMeta?.totalCount ?? totalCount ?? rows.length;
  const safePageSize = pageSize || DEFAULT_PAGE_SIZE;
  const startIndex = displayTotal === 0 ? 0 : (page - 1) * safePageSize + 1;
  const endIndex = displayTotal === 0 ? 0 : Math.min(displayTotal, startIndex + rows.length - 1);
  const pageCount = paginationMeta?.pageCount ?? Math.max(1, Math.ceil(displayTotal / safePageSize));
  const hasPrevPage = paginationMeta?.hasPrevPage ?? page > 1;
  const hasNextPage = paginationMeta?.hasNextPage ?? page < pageCount;
  const selectionLock = selectedCount > 1;
  const paginationDisabled = !onPageChange;
  const pageSizeDisabled = !onPageSizeChange;
  const computedTabs = tabs ?? (headerLabel ? [{ id: '__list', label: headerLabel }] : undefined);
  const effectiveActiveTabId = computedTabs ? (activeTabId ?? computedTabs[0].id) : undefined;
  const handleTabChange = (id: string) => {
    if (!tabs) {
      return;
    }
    onTabChange?.(id);
  };
  const tabsMarkup = computedTabs ? (
    <Tabs items={computedTabs} activeId={effectiveActiveTabId ?? computedTabs[0].id} onChange={handleTabChange} />
  ) : null;
  const selectionLabel = selectedCount > 0 ? <span className="list-workspace__selection-count">{selectedCount} selected</span> : null;
  const hasToolbarContent = Boolean(
    tabsMarkup ||
      toolbarLeadingActions ||
      selectionLabel ||
      toolbarActions ||
      onAdd ||
      onDeleteSelected ||
      filterBar ||
      onRefresh ||
      auditPanel
  );

  const iconButtonClass = (isDanger?: boolean, active?: boolean) =>
    [
      'list-workspace__icon-button',
      isDanger ? 'is-danger' : '',
      active ? 'is-active' : '',
      selectionLock && !isDanger ? 'is-disabled' : ''
    ]
      .filter(Boolean)
      .join(' ');

  const renderTable = () => {
    if (!loading && rows.length === 0) {
      return <div className="list-workspace__empty">{emptyState ?? <p>No records to display.</p>}</div>;
    }

    return (
      <div className="list-workspace__table-container">
        <table className="list-workspace__table">
          <thead>
            <tr>
              {!selectionDisabled && (
                <th className="list-workspace__cell--checkbox">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    aria-label="Select all rows"
                    onChange={(event) => handleSelectAll(event.target.checked)}
                  />
                </th>
              )}
              {columns.map((column) => (
                <th key={column.key} style={column.width ? { width: column.width } : undefined} className={column.align ? `is-${column.align}` : ''}>
                  {column.header}
                </th>
              ))}
              {rowActions && <th className="list-workspace__cell--actions">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => {
              const id = rowId(row, index);
              const isSelected = selectedIds.has(id);
              const isActive = highlightRowId != null && highlightRowId === id;
              const isHovered = hoveredRowId === id;
              const rowClass = [
                'list-workspace__row',
                index % 2 === 1 ? 'list-workspace__row--shaded' : '',
                isSelected ? 'is-selected' : '',
                isActive ? 'is-active' : '',
                onRowClick ? 'is-actionable' : '',
                isHovered ? 'is-hovered' : ''
              ]
                .filter(Boolean)
                .join(' ');
              return (
                <tr
                  key={id}
                  className={rowClass}
                  onClick={() => onRowClick?.(row)}
                  onMouseEnter={() => setHoveredRowId(id)}
                  onMouseLeave={() => {
                    setHoveredRowId((previous) => (previous === id ? null : previous));
                  }}
                >
                  {!selectionDisabled && (
                    <td className="list-workspace__cell--checkbox" onClick={(event) => event.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => handleToggleRow(id)}
                        aria-label={isSelected ? 'Deselect row' : 'Select row'}
                      />
                    </td>
                )}
                {columns.map((column) => (
                  <td key={column.key} className={column.align ? `is-${column.align}` : ''}>
                    {column.render(row, index)}
                  </td>
                ))}
                {rowActions && (
                  <td className="list-workspace__cell--actions" onClick={(event) => event.stopPropagation()}>
                    <div className="list-workspace__row-actions">{rowActions(row, index)}</div>
                  </td>
                )}
              </tr>
            );
          })}
          </tbody>
        </table>
        {loading && (
          <div className="list-workspace__loading">
            <span>Loading…</span>
          </div>
        )}
      </div>
    );
  };

  return (
    <section className="list-workspace">
      {!collapseToolbarWhenEmpty || hasToolbarContent ? (
        <div className="list-workspace__toolbar">
          <div className="list-workspace__tabs-bar">
            {tabsMarkup}
            {toolbarLeadingActions && <div className="list-workspace__toolbar-leading">{toolbarLeadingActions}</div>}
            {selectionLabel && <div className="list-workspace__selection-state">{selectionLabel}</div>}
          </div>
          <div className="list-workspace__actions">
            {toolbarActions}
            {onAdd && (
              <button
                type="button"
                className={iconButtonClass()}
                onClick={onAdd}
                disabled={selectionLock}
                aria-label="Add entry"
                title="Add entry"
              >
                <PlusIcon size={18} />
              </button>
            )}
            {onDeleteSelected && (
              <button
                type="button"
                className={iconButtonClass(true)}
                onClick={() => selectedRows.length > 0 && onDeleteSelected(selectedRows)}
                disabled={selectedRows.length === 0}
                aria-label="Delete selected entries"
                title="Delete selected entries"
              >
                <TrashIcon size={18} />
              </button>
            )}
            {filterBar && (
              <button
                type="button"
                className={iconButtonClass(false, filtersOpen || filterActive)}
                onClick={handleToggleFilters}
                disabled={selectionLock}
                aria-label="Toggle filters"
                title="Toggle filters"
              >
                <FilterIcon size={18} />
              </button>
            )}
            {onRefresh && (
              <button
                type="button"
                className={iconButtonClass()}
                onClick={onRefresh}
                disabled={selectionLock}
                aria-label="Refresh"
                title="Refresh"
              >
                <RefreshIcon size={18} />
              </button>
            )}
            {auditPanel && (
              <button
                type="button"
                className={iconButtonClass(false, auditOpen)}
                onClick={handleToggleAudit}
                disabled={selectionLock}
                aria-label="Toggle audit panel"
                title="Toggle audit panel"
              >
                <ActivityIcon size={18} />
              </button>
            )}
          </div>
        </div>
      ) : null}
      {headerStatus && <div className="list-workspace__status">{headerStatus}</div>}
      {filterBar && filtersOpen && (
        <div className="list-workspace__filter-bar">
          <div className="list-workspace__filter-header">
            <div>
              <p className="list-workspace__filter-title">Filters</p>
              <p className="list-workspace__filter-subtitle">Narrow the list by applying one or more filters.</p>
            </div>
            <div className="list-workspace__filter-actions">
              <button type="button" className="ui-button ui-button--ghost" onClick={onFilterReset}>
                Reset filters
              </button>
              <button type="button" className="ui-button" onClick={handleToggleFilters}>
                Close
              </button>
            </div>
          </div>
          <div className="list-workspace__filter-body">{filterBar}</div>
        </div>
      )}
      <div className="list-workspace__body">{renderTable()}</div>
      {!hidePagination && (
        <footer className="list-workspace__footer">
          <div className="list-workspace__range">
            {displayTotal === 0 ? 'No entries' : `${startIndex}-${endIndex} of ${displayTotal} entries`}
          </div>
          <div className="list-workspace__pager">
            <label className="list-workspace__page-size">
              Rows per page
              <select value={safePageSize} onChange={(event) => onPageSizeChange?.(parseInt(event.target.value, 10) || safePageSize)} disabled={pageSizeDisabled}>
                {pageSizeOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </label>
            <div className="list-workspace__pagination">
              <button
                type="button"
                className="list-workspace__icon-button"
                onClick={() => onPageChange?.(Math.max(1, page - 1))}
                disabled={paginationDisabled || !hasPrevPage || page <= 1}
                aria-label="Previous page"
              >
                <ChevronLeftIcon size={18} />
              </button>
              <span>
                Page {page} of {pageCount}
              </span>
              <button
                type="button"
                className="list-workspace__icon-button"
                onClick={() => onPageChange?.(Math.min(pageCount, page + 1))}
                disabled={paginationDisabled || !hasNextPage || page >= pageCount}
                aria-label="Next page"
              >
                <ChevronRightIcon size={18} />
              </button>
            </div>
          </div>
        </footer>
      )}
      {auditPanel && auditOpen && <div className="list-workspace__audit-panel">{auditPanel}</div>}
    </section>
  );
};

export default ListWorkspace;
